import { ConfigOptions, CsvOutput, mkConfig, generateCsv, asBlob } from 'export-to-csv'
import { Row } from '@tanstack/react-table'

import { ISODateStringToYYYYMMDD } from '@/utils/util'
import { DownloadBlob } from '@/components/components'
import { LeaDataModel, EffectiveDateRecord, ENRL, CLEN, ATTN, LANG, SLNG, RecordType } from '@/models'
import { CHLD } from '@/models/chld'
import { FAMI } from '@/models/fami'
import { STFF, asSerializeable, asSerializeableSASS, asSerializeableSCLR } from '@/models/stff'
import { CLRM } from '@/models/clrm'

export const csvConfigs: Record<RecordType, Required<ConfigOptions>> = {
  CHLD: mkConfig({
    fieldSeparator: ',',
    filename: 'sample', // export file name (without .csv)
    decimalSeparator: '.',
    showColumnHeaders: false,
    columnHeaders: ['RecordTypeCode', 'TransactionTypeCode', 'ReportingLEA', 'PreschoolCode', 'ProgramYear',
      'CSPPID', 'SSID', 'LocalChildId', 'ChildFirstName', 'ChildMiddleName',
      'ChildLastName', 'ChildNameSuffix', 'ChildDateOfBirth', 'ChildGender', 'ChildEthnicity',
      'ChildEthnicityMissingIndicator', 'ChildRace1', 'ChildRace2', 'ChildRace3', 'ChildRace4',
      'ChildRace5', 'ChildRaceMissingIndicator', 'ChildCityOfBirth', 'ChildStateOfBirth', 'ChildCountryOfBirth',
      'IEPIFSPIndicator', 'EffectiveStartDateString'],
  }),
  FAMI: mkConfig({
    fieldSeparator: ',',
    filename: 'family', // export file name (without .csv)
    decimalSeparator: '.',
    showColumnHeaders: false,
    columnHeaders: ['RecordTypeCode', 'TransactionTypeCode', 'ReportingLEA', 'PreschoolCode', 'ProgramYear',
      'LocalFamilyId', 'CSPPID', 'LocalChildId', 'HeadOfHousehold1FirstName', 'HeadOfHousehold1MiddleName',
      'HeadOfHousehold1LastName', 'HeadOfHousehold2FirstName', 'HeadOfHousehold2MiddleName', 'HeadOfHousehold2LastName', 'HeadOfHousehold1HighestEducationLevel',
      'HeadOfHousehold2HighestEducationLevel', 'ResidentialAddress1', 'ResidentialAddress2', 'ResidentialCity', 'ResidentialZip',
      'ResidentialState', 'MailingAddress1', 'MailingAddress2', 'MailingCity', 'MailingZip',
      'MailingState', 'HeadOfHouseholdWrittenCommunicationPreference', 'HeadOfHouseholdVerbalCommunicationPreference', 'FamilyIncome', 'HeadOfHousehold1NeedForService1',
      'HeadOfHousehold1NeedForService2', 'HeadOfHousehold1NeedForService3', 'HeadOfHousehold1NeedForService4', 'HeadOfHousehold1NeedForService5', 'HeadOfHousehold1NeedForService6',
      'HeadOfHousehold1NeedForService7', 'HeadOfHousehold1NeedForService8', 'HeadOfHousehold1NeedForService9', 'HeadOfHousehold2NeedForService1', 'HeadOfHousehold2NeedForService2',
      'HeadOfHousehold2NeedForService3', 'HeadOfHousehold2NeedForService4', 'HeadOfHousehold2NeedForService5', 'HeadOfHousehold2NeedForService6', 'HeadOfHousehold2NeedForService7',
      'HeadOfHousehold2NeedForService8', 'HeadOfHousehold2NeedForService9', 'CALWorksRecipient', 'FamilySize', 'EffectiveStartDate'],
  }),
  CLEN: mkConfig({
    fieldSeparator: ',',
    filename: 'sample', // export file name (without .csv)
    decimalSeparator: '.',
    showColumnHeaders: false,
    columnHeaders: ['RecordTypeCode', 'TransactionTypeCode', 'ReportingLEA', 'PreschoolCode', 'ProgramYear',
      'CSPPID', 'LocalChildId', 'LocalClassroomId'],
  }),
  //LANG: addNewLanguages,
  //SLNG: 'Supplemental DLL (SLNG)',
  STFF: mkConfig({
    fieldSeparator: ',',
    filename: 'sample', // export file name (without .csv)
    decimalSeparator: '.',
    showColumnHeaders: false,
    columnHeaders: [
      'RecordTypeCode', 'TransactionTypeCode', 'ReportingLEA', 'ProgramYear', 'StaffLegalFirstName', 
      'StaffLegalMiddleName', 'StaffLegalLastName', 'SEID', 'LocalStaffId', 'StaffPermitOrCredentialNumber',
      'StaffPermitOrCredentialType', 'StaffPermitIssuedDate', 'StaffPermitExpirationDate', 'StaffWaiver', 'StaffWaiverType',
      'StaffWaiverIssuedDate', 'StaffWaiverExpirationDate', 'StaffDateOfBirth', 'StaffGender', 'StaffEthnicity',
      'StaffEthnicityMissingIndicator', 'StaffRace1', 'StaffRace2', 'StaffRace3', 'StaffRace4',
      'StaffRace5', 'StaffRaceMissingIndicator', 'StaffHighestDegreeCode', 'StaffLanguage1', 'StaffLanguage2',
      'StaffLanguage3', 'StaffLanguage4', 'StaffLanguage5', 'StaffCurrentEmploymentStartDate', 'StaffCurrentEmploymentEndDate',
      'StaffOverallEmploymentStartDate', 'EffectiveStartDate'
    ]
  }),
  SASS: mkConfig({
    fieldSeparator: ',',
    filename: 'sample', // export file name (without .csv)
    decimalSeparator: '.',
    showColumnHeaders: false,
    columnHeaders: ['RecordTypeCode', 'TransactionTypeCode', 'ReportingLEA', 'PreschoolCode', 'ProgramYear',
      'LocalStaffId', 'ClassroomRole1', 'ClassroomRole1PercentOfTime', 'ClassroomRole2', 'ClassroomRole2PercentOfTime',
      'NonClassroomRole1', 'NonClassroomRole1PercentOfTime', 'NonClassroomRole2', 'NonClassroomRole2PercentOfTime', 'NonClassroomRole3',
      'NonClassroomRole3PercentOfTime'],
  }),
  CLRM: mkConfig({
    fieldSeparator: ',',
    filename: 'classroom', // export file name (without .csv)
    decimalSeparator: '.',
    showColumnHeaders: false,
    columnHeaders: ['RecordTypeCode', 'TransactionTypeCode', 'ReportingLEA', 'PreschoolCode', 'ProgramYear',
      'ClassroomName', 'LocalClassroomId', 'VolunteerFrequency', 'ProgramTypeOffered1', 'ProgramTypeOffere2',
      'ProgramDurationOffered1', 'ProgramDurationOffered2', 'ContractTypeOffered1', 'ContractTypeOffered2', 'SettingTypeOffered',
      'MHCSOffered', 'LanguageProgramType', 'LanguageUsedInClassroom1', 'LanguageUsedInClassroom2', 'LanguageUsedInClassroom3',
      'LanguageUsedInClassroom4', 'LanguageUsedInClassroom5', 'EffectiveStartDate'],
  }),
  SCLR: mkConfig({
    fieldSeparator: ',',
    filename: 'sample', // export file name (without .csv)
    decimalSeparator: '.',
    showColumnHeaders: false,
    columnHeaders: ['RecordTypeCode', 'TransactionTypeCode', 'ReportingLEA', 'PreschoolCode', 'ProgramYear',
      'LocalStaffId', 'LocalClassroomId'],
  }),
  ENRL: mkConfig({
    fieldSeparator: ',',
    filename: 'sample', // export file name (without .csv)
    decimalSeparator: '.',
    showColumnHeaders: false,
    columnHeaders: ['RecordTypeCode', 'TransactionTypeCode', 'ReportingLEA', 'PreschoolCode', 'ProgramYear',
      'CSPPID', 'LocalChildId', 'ContractType', 'EnrollmentStartDate', 'EnrollmentEndDate',
      'EnrollmentEndReason', 'ProgramType', 'ProgramDuration', 'EligibilityStatus'],
  }),
  ATTN: mkConfig({
    fieldSeparator: ',',
    filename: 'sample', // export file name (without .csv)
    decimalSeparator: '.',
    showColumnHeaders: false,
    columnHeaders: ['ReportingLEA', 'PreschoolCode', 'LocalRecordId', 'LocalFamilyId', 'CSPPID', 'LocalClassroomId',
      'FirstOfMonthString', 'DaysExpectedString', 'DaysAttendedString', 'DaysAbsentExcusedString', 'DaysAbsentUnexcusedString', 'DaysSuspendedString'],
  }),
}

export function chldExportCSV(rows: CHLD[]) {
    const chldCsvConfig = csvConfigs['CHLD']
    const rowData = rows.map((chld) => {
      if (ISODateStringToYYYYMMDD(chld.EffectiveStartDate) == "20250630") {
        console.log("here")
      }
      return {
        'RecordTypeCode': 'CHLD',
        'TransactionTypeCode': '',
        'ReportingLEA': chld.ReportingLEA,
        'PreschoolCode': chld.PreschoolCode,
        'ProgramYear': chld.ProgramYear,
        'CSPPID': chld.CSPPID,
        'SSID': chld.SSID,
        'LocalChildId': chld.LocalChildId,
        'ChildFirstName': chld.ChildFirstName,
        'ChildMiddleName': chld.ChildMiddleName,
        'ChildLastName': chld.ChildLastName,
        'ChildNameSuffix': chld.ChildNameSuffix,
        'ChildDateOfBirth': ISODateStringToYYYYMMDD(chld.ChildDateOfBirth),
        'ChildGender': chld.ChildGender,
        'ChildEthnicity': chld.ChildEthnicity || '',
        'ChildEthnicityMissingIndicator': chld.ChildEthnicityMissingIndicator,
        'ChildRace1': chld.ChildRace1,
        'ChildRace2': chld.ChildRace2,
        'ChildRace3': chld.ChildRace3,
        'ChildRace4': chld.ChildRace4,
        'ChildRace5': chld.ChildRace5,
        'ChildRaceMissingIndicator': chld.ChildRaceMissingIndicator,
        'ChildCityOfBirth': chld.ChildCityOfBirth,
        'ChildCountryOfBirth': chld.ChildCountryOfBirth,
        'ChildStateOfBirth': chld.ChildStateOfBirth,
        'IEPIFSPIndicator': chld.IEPIFSPIndicator,
        'EffectiveStartDateString': ISODateStringToYYYYMMDD(chld.EffectiveStartDate),
      }
    })
    const childRecords = Array.prototype.concat.apply([], rowData)
    const csv = generateCsv(chldCsvConfig)(childRecords)
    DownloadBlob(asBlob(chldCsvConfig)(csv), 'child.csv')
}

export function enrlExportCSV(rows: Row<CHLD>[]) {
    const rowData = rows.map((row) => row.original.Enrollments.map((enrl: ENRL) => ({
        'RecordTypeCode': 'ENRL',
        'TransactionTypeCode': '',
        'ReportingLEA': enrl.ReportingLEA,
        'PreschoolCode': enrl.PreschoolCode,
        'ProgramYear': enrl.ProgramYear,
        'CSPPID': enrl.CSPPID,
        'LocalChildId': enrl.LocalChildId,
        'ContractType': enrl.ContractType,
        'EnrollmentStartDate': ISODateStringToYYYYMMDD(enrl.EnrollmentStartDate),
        'EnrollmentEndDate': ISODateStringToYYYYMMDD(enrl.EnrollmentEndDate) ?? '',
        'EnrollmentEndReason': enrl.EnrollmentEndReason,
        'ProgramType': enrl.ProgramType,
        'ProgramDuration': enrl.ProgramDuration,
        //'TimeBase': enrl.TimeBase,
        'EligibilityStatus': enrl.EligibilityStatus,
    })))
    const enrls = Array.prototype.concat.apply([], rowData)
    const csv = generateCsv(enrlCsvConfig)(enrls)
    DownloadBlob(asBlob(enrlCsvConfig)(csv), 'enrl.csv')
}

export function clenExportCSV(rows: Row<CHLD>[]) {
    const rowData = rows.map((row) => row.original.ClassroomAssignments.map((clen: CLEN) => ({
        'RecordTypeCode': 'CLEN',
        'TransactionTypeCode': '',
        'ReportingLEA': clen.ReportingLEA,
        'PreschoolCode': clen.PreschoolCode,
        'ProgramYear': clen.ProgramYear,
        'CSPPID': clen.CSPPID,
        'LocalChildId': clen.LocalChildId,
        'LocalClassroomId': clen.LocalClassroomId,
    })))
    const clens = Array.prototype.concat.apply([], rowData)
    const csv = generateCsv(clenCsvConfig)(clens)
    DownloadBlob(asBlob(clenCsvConfig)(csv), 'clen.csv')
}

export function attnExportCSV(rows: Row<CHLD>[]) {
    const rowData = rows.map((row) => row.original.Attendance.map((attn) => ({
        'ReportingLEA': attn.ReportingLEA,
        'PreschoolCode': attn.PreschoolCode,
        //'FiscalYear': attn.fiscalYear,
        'LocalRecordId': '',
        'LocalFamilyId': attn.LocalFamilyId,
        'CSPPID': attn.CSPPID,
        'LocalClassroomId': attn.LocalClassroomId,
        'FirstOfMonthString': ISODateStringToYYYYMMDD(attn.FirstOfMonth),
        'DaysExpectedString': attn.DaysExpected.toString(),
        'DaysAttendedString': attn.DaysAttended.toString(),
        'DaysAbsentExcusedString': attn.DaysAbsentExcused.toString(),
        'DaysAbsentUnexcusedString': attn.DaysAbsentUnexcused.toString(),
        'DaysSuspendedString': attn.DaysSuspended.toString(),
    })))
    const attns = Array.prototype.concat.apply([], rowData)
    const csv = generateCsv(attnCsvConfig)(attns)
    DownloadBlob(asBlob(attnCsvConfig)(csv), 'attn.csv')
}

export function famiExportCSV(rows: Row<FAMI>[]) {
  const rowData = rows.map((row) => ({
    'RecordTypeCode': 'FAMI',
    'TransactionTypeCode': '',
    'ReportingLEA': row.original.ReportingLEA,
    'PreschoolCode': row.original.PreschoolCode,
    'ProgramYear': row.original.ProgramYear,
    'LocalFamilyId': row.original.LocalFamilyId,
    'CSPPID': row.original.CSPPID,
    'LocalChildId': row.original.LocalChildId,
    'HeadOfHousehold1FirstName': row.original.HeadOfHousehold1FirstName,
    'HeadOfHousehold1MiddleName': row.original.HeadOfHousehold1MiddleName,
    'HeadOfHousehold1LastName': row.original.HeadOfHousehold1LastName,
    'HeadOfHousehold2FirstName': row.original.HeadOfHousehold2FirstName,
    'HeadOfHousehold2MiddleName': row.original.HeadOfHousehold2MiddleName,
    'HeadOfHousehold2LastName': row.original.HeadOfHousehold2LastName,
    'HeadOfHousehold1HighestEducationLevel': row.original.HeadOfHousehold1HighestEducationLevel,
    'HeadOfHousehold2HighestEducationLevel': row.original.HeadOfHousehold2HighestEducationLevel,
    'ResidentialAddress1': row.original.ResidentialAddress1,
    'ResidentialAddress2': row.original.ResidentialAddress2,
    'ResidentialCity': row.original.ResidentialCity,
    'ResidentialZip': row.original.ResidentialZip,
    'ResidentialState': row.original.ResidentialState,
    'MailingAddress1': row.original.MailingAddress1,
    'MailingAddress2': row.original.MailingAddress2,
    'MailingCity': row.original.MailingCity,
    'MailingZip': row.original.MailingZip,
    'MailingState': row.original.MailingState,
    'HeadOfHouseholdWrittenCommunicationPreference': row.original.HeadOfHouseholdWrittenCommunicationPreference,
    'HeadOfHouseholdVerbalCommunicationPreference': row.original.HeadOfHouseholdVerbalCommunicationPreference,
    'FamilyIncome': row.original.FamilyIncome,
    'HeadOfHousehold1NeedForService1': row.original.HeadOfHousehold1NeedForService1,
    'HeadOfHousehold1NeedForService2': row.original.HeadOfHousehold1NeedForService2,
    'HeadOfHousehold1NeedForService3': row.original.HeadOfHousehold1NeedForService3,
    'HeadOfHousehold1NeedForService4': row.original.HeadOfHousehold1NeedForService4,
    'HeadOfHousehold1NeedForService5': row.original.HeadOfHousehold1NeedForService5,
    'HeadOfHousehold1NeedForService6': row.original.HeadOfHousehold1NeedForService6,
    'HeadOfHousehold1NeedForService7': row.original.HeadOfHousehold1NeedForService7,
    'HeadOfHousehold1NeedForService8': row.original.HeadOfHousehold1NeedForService8,
    'HeadOfHousehold1NeedForService9': row.original.HeadOfHousehold1NeedForService9,
    'HeadOfHousehold2NeedForService1': row.original.HeadOfHousehold2NeedForService1,
    'HeadOfHousehold2NeedForService2': row.original.HeadOfHousehold2NeedForService2,
    'HeadOfHousehold2NeedForService3': row.original.HeadOfHousehold2NeedForService3,
    'HeadOfHousehold2NeedForService4': row.original.HeadOfHousehold2NeedForService4,
    'HeadOfHousehold2NeedForService5': row.original.HeadOfHousehold2NeedForService5,
    'HeadOfHousehold2NeedForService6': row.original.HeadOfHousehold2NeedForService6,
    'HeadOfHousehold2NeedForService7': row.original.HeadOfHousehold2NeedForService7,
    'HeadOfHousehold2NeedForService8': row.original.HeadOfHousehold2NeedForService8,
    'HeadOfHousehold2NeedForService9': row.original.HeadOfHousehold2NeedForService9,
    'CALWorksRecipient': row.original.CALWorksRecipient,
    'FamilySize': row.original.FamilySize,
    'EffectiveStartDate': ISODateStringToYYYYMMDD(row.original.EffectiveStartDate),
  }))
  const csv = generateCsv(famiCsvConfig)(rowData)
  DownloadBlob(asBlob(famiCsvConfig)(csv), 'family.csv')
}

export function stffExportCSV(rows: STFF[]) {
  const stffCsvConfig = csvConfigs['STFF']
  const rowData = rows.map((staff) => {
    return {
      'RecordTypeCode': 'STFF',
      'TransactionTypeCode': '',
      Id: staff.Id,
      ReportingLEA: staff.ReportingLEA,
      ProgramYear: staff.ProgramYear,
      StaffLegalFirstName: staff.StaffLegalFirstName,
      StaffLegalMiddleName: staff.StaffLegalMiddleName,
      StaffLegalLastName: staff.StaffLegalLastName,
      SEID: staff.SEID,
      LocalStaffId: staff.LocalStaffId,
      StaffDateOfBirth: ISODateStringToYYYYMMDD(staff.StaffDateOfBirth),
      StaffGender: staff.StaffGender,
      StaffEthnicity: staff.StaffEthnicity,
      StaffEthnicityMissingIndicator: staff.StaffEthnicityMissingIndicator,
      StaffRace1: staff.StaffRace1,
      StaffRace2: staff.StaffRace2,
      StaffRace3: staff.StaffRace3,
      StaffRace4: staff.StaffRace4,
      StaffRace5: staff.StaffRace5,
      StaffRaceMissingIndicator: staff.StaffRaceMissingIndicator,
      StaffLanguage1: staff.StaffLanguage1,
      StaffLanguage2: staff.StaffLanguage2,
      StaffLanguage3: staff.StaffLanguage3,
      StaffLanguage4: staff.StaffLanguage4,
      StaffLanguage5: staff.StaffLanguage5,
      StaffHighestDegreeCode: staff.StaffHighestDegreeCode,
      StaffWaiver: staff.StaffWaiver,
      StaffWaiverType: staff.StaffWaiverType,
      StaffWaiverIssuedDate: ISODateStringToYYYYMMDD(staff.StaffWaiverIssuedDate),
      StaffWaiverExpirationDate: ISODateStringToYYYYMMDD(staff.StaffWaiverExpirationDate),
      StaffPermitOrCredentialNumber: staff.StaffPermitOrCredentialNumber,
      StaffPermitOrCredentialType: staff.StaffPermitOrCredentialType,
      StaffPermitIssuedDate: ISODateStringToYYYYMMDD(staff.StaffPermitIssuedDate),
      StaffPermitExpirationDate: ISODateStringToYYYYMMDD(staff.StaffPermitExpirationDate),
      StaffCurrentEmploymentStartDate: ISODateStringToYYYYMMDD(staff.StaffCurrentEmploymentStartDate),
      StaffCurrentEmploymentEndDate: ISODateStringToYYYYMMDD(staff.StaffCurrentEmploymentEndDate),
      StaffOverallEmploymentStartDate: ISODateStringToYYYYMMDD(staff.StaffOverallEmploymentStartDate),
      EffectiveStartDate: ISODateStringToYYYYMMDD(staff.EffectiveStartDate),
    }
  })
  const csv = generateCsv(stffCsvConfig)(rowData)
  DownloadBlob(asBlob(stffCsvConfig)(csv), 'staff.csv')
}

export function sassExportCSV(rows: Row<STFF>[]) {
  const rowData = rows.map((row) => (row.original.StaffAssignments === undefined ? [] : [{
    ...asSerializeableSASS(row.original.StaffAssignments),
    ...{
      'RecordTypeCode': 'SASS',
      'TransactionTypeCode': '',
    }
  }]))
  const sassRecords = Array.prototype.concat.apply([], rowData)
  const csv = generateCsv(sassCsvConfig)(sassRecords)
  DownloadBlob(asBlob(sassCsvConfig)(csv), 'sass.csv')
}

export function sclrExportCSV(rows: Row<STFF>[]) {
  const rowData = rows.map((row) => row.original.ClassroomAssignments.map((sclr) => ({
    'RecordTypeCode': 'SCLR',
    'TransactionTypeCode': '',
    'ReportingLEA': sclr.ReportingLEA,
    'PreschoolCode': sclr.PreschoolCode,
    'ProgramYear': sclr.ProgramYear,
    'LocalClassroomId': sclr.LocalClassroomId,
    'LocalStaffId': sclr.LocalStaffId,
  })))
  const sclrs = Array.prototype.concat.apply([], rowData)
  const csv = generateCsv(sclrCsvConfig)(sclrs)
  DownloadBlob(asBlob(sclrCsvConfig)(csv), 'sclr.csv')
}

export function clrmExportCSV(rows: CLRM[]) {
  const clrmCsvConfig = csvConfigs['CLRM']
  const rowData = rows.map((clrm) => {
    return {
      'RecordTypeCode': 'CLRM',
      'TransactionTypeCode': '',
      'ReportingLEA': clrm.ReportingLEA,
      'PreschoolCode': clrm.PreschoolCode,
      'ProgramYear': clrm.ProgramYear,
      'ClassroomName': clrm.ClassroomName,
      'LocalClassroomId': clrm.LocalClassroomId,
      'VolunteerFrequency': clrm.VolunteerFrequency,
      'ProgramTypeOffered1': clrm.ProgramTypeOffered1,
      'ProgramTypeOffered2': clrm.ProgramTypeOffered2,
      'ProgramDurationOffered1': clrm.ProgramDurationOffered1,
      'ProgramDurationOffered2': clrm.ProgramDurationOffered2,
      'ContractTypeOffered1': clrm.ContractTypeOffered1,
      'ContractTypeOffered2': clrm.ContractTypeOffered2,
      'SettingTypeOffered': clrm.SettingTypeOffered,
      'MHCSOffered': clrm.MHCSOffered,
      'LanguageProgramType': clrm.LanguageProgramType,
      'LanguageUsedInClassroom1': clrm.LanguageUsedInClassroom1,
      'LanguageUsedInClassroom2': clrm.LanguageUsedInClassroom2 ?? "",
      'LanguageUsedInClassroom3': clrm.LanguageUsedInClassroom3 ?? "",
      'LanguageUsedInClassroom4': clrm.LanguageUsedInClassroom4 ?? "",
      'LanguageUsedInClassroom5': clrm.LanguageUsedInClassroom5 ?? "",
      'EffectiveStartDate': ISODateStringToYYYYMMDD(clrm.EffectiveStartDate),
    }
  })
  const csv = generateCsv(clrmCsvConfig)(rowData)
  DownloadBlob(asBlob(clrmCsvConfig)(csv), 'classroom.csv')
}
